import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useWritesRefused } from '../components/SuspendedNotice.js';
import { Alert, Card } from '../components/ui.js';
import { formatCredits, formatDate } from '../lib/format.js';
import { orderTypeLabel, statusLabel, statusToneClasses } from '../lib/orderDisplay.js';
import { fetchOrders, type OrderListItem } from '../lib/ordersApi.js';

export function OrdersPage(): React.JSX.Element {
  // Suspended, or an operator viewing read-only - WAIT-B defect 3.
  const { refused: suspended } = useWritesRefused();
  const [orders, setOrders] = useState<OrderListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const response = await fetchOrders();
        if (!cancelled) setOrders(response.orders);
      } catch {
        if (!cancelled) setError('We could not load your orders.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Orders</h1>
          <p className="mt-1 text-sm text-fg-muted">Every build you have asked us for.</p>
        </div>
        {suspended ? null : (
        <Link
          to="/orders/new"
          className="shrink-0 rounded-control bg-blue px-4 py-2.5 text-sm font-semibold text-ground transition hover:bg-blue-bright"
        >
          Place an order
        </Link>
        )}
      </div>

      {error ? <Alert>{error}</Alert> : null}

      <Card className="p-0">
        {loading ? (
          <p className="p-6 text-sm text-fg-muted" role="status">
            Loading…
          </p>
        ) : orders.length > 0 ? (
          <ul className="divide-y divide-rule-soft">
            {orders.map((order) => (
              <li key={order.id} className="flex items-center gap-4 px-6 py-4">
                <div className="min-w-0 flex-1">
                  <Link
                    to={`/orders/${order.id}`}
                    className="text-sm font-medium text-fg underline"
                  >
                    {order.businessName}
                  </Link>
                  <p className="mt-0.5 text-sm text-fg-muted">
                    {orderTypeLabel(order.orderType)} · {formatCredits(order.creditsCost)} ·{' '}
                    {formatDate(order.createdAt)}
                  </p>
                </div>
                <span
                  className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${statusToneClasses(
                    order.status,
                  )}`}
                >
                  {statusLabel(order.status)}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="p-6">
            <p className="text-sm font-medium text-fg">No orders yet</p>
            <p className="mt-1 text-sm text-fg-muted">
              Add a client, then order their first site build.
            </p>
            {suspended ? null : (
            <Link
              to="/orders/new"
              className="mt-4 inline-block text-sm font-medium text-fg underline"
            >
              Place your first order
            </Link>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
